import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Map, Calculator } from 'lucide-react'
import { useDcStore } from '../stores/dcStore'
import { useCalculatorLogic, rackLabel, type CalcResult } from '../hooks/useCalculatorLogic'
import { RackParametersPanel } from '../components/calculator/RackParametersPanel'
import { SummaryPanel } from '../components/calculator/SummaryPanel'
import { DcMapSVG } from '../components/calculator/DcMapSVG'
import { selectCalculationRoute, sharedRouteSegments } from '../utils/calculatorRoute.logic'
import '../components/calculator/calculator.css'

export default function CalculatorPage() {
  const { code: dcCode } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { datacenters: dcs, loading: dcLoading } = useDcStore()
  const currentDc = dcs.find(d => d.code === dcCode)

  useEffect(() => {
    if (!dcLoading && dcs.length > 0 && !currentDc) {
      toast.error(`Datacenter "${dcCode}" not found.`)
      navigate('/')
    }
  }, [dcLoading, currentDc, dcCode, dcs.length, navigate])

  const {
    racks, racksLoading, src, setSrc, srcU, setSrcU, tgt, setTgt, tgtU, setTgtU,
    cable, setCable, loading, error, result, setResult,
    history, restoreHistory, handleSwap, handleCalc, handleRackClick
  } = useCalculatorLogic(dcCode, currentDc)
  const [selection, setSelection] = useState<{ result: CalcResult | null; index: number }>({ result: null, index: 0 })
  const selectedIndex = selection.result === result ? selection.index : 0
  const selectedResult = selectCalculationRoute(result, selectedIndex)
  const [parametersOpen, setParametersOpen] = useState(true)
  const [mobileView, setMobileView] = useState<'plan' | 'calculation'>('calculation')
  useEffect(() => {
    setParametersOpen(!result)
    if (result) setMobileView('plan')
  }, [result])

  return (
    <div className="calculator-workbench" data-mobile-view={mobileView}>
      <header className="calculator-header">
        <h1>Калькулятор кабелей</h1>
        <div className="calculator-mobile-tabs" role="tablist" aria-label="Вид калькулятора" onKeyDown={event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
          event.preventDefault()
          const next = event.key === 'Home' ? 'calculation' : event.key === 'End' ? 'plan' : mobileView === 'plan' ? 'calculation' : 'plan'
          setMobileView(next)
          event.currentTarget.querySelector<HTMLButtonElement>(`#${next === 'plan' ? 'plan' : 'calculation'}-tab`)?.focus()
        }}>
          <button type="button" role="tab" id="calculation-tab" aria-controls="calculation-panel" tabIndex={mobileView === 'calculation' ? 0 : -1} aria-selected={mobileView === 'calculation'} onClick={() => setMobileView('calculation')}><Calculator size={15} />Расчёт</button>
          <button type="button" role="tab" id="plan-tab" aria-controls="plan-panel" tabIndex={mobileView === 'plan' ? 0 : -1} aria-selected={mobileView === 'plan'} onClick={() => setMobileView('plan')}><Map size={15} />План</button>
        </div>
      </header>
      {selectedResult && src && tgt && <div className="calculator-result-strip" aria-live="polite">
        <div className="calculator-connection"><span><b>A</b> {rackLabel(src, currentDc)} <em>U{srcU}</em></span><span><b>B</b> {rackLabel(tgt, currentDc)} <em>U{tgtU}</em></span></div>
        <div className="calculator-lengths">
          <div data-testid="calculation-length"><span>Расчёт с запасом</span><strong>{selectedResult.length_evidence?.exact_length_m.toFixed(2) ?? '—'} <small>м</small></strong></div>
          <div data-testid="order-length"><span>К заказу</span><strong>{selectedResult.total_length.toFixed(2)} <small>м</small></strong></div>
        </div>
      </div>}
      <div className="calculator-body">
      {/* ── Left sidebar (params + results) ── */}
      <section id="calculation-panel" aria-label="Расчёт маршрута" className="calculator-sidebar">

        {/* ── Params section ── */}
        <details open={parametersOpen} onToggle={event => setParametersOpen(event.currentTarget.open)} className="shrink-0">
          <summary className="px-3 py-2 text-xs text-text-secondary cursor-pointer border-b border-border">
            Параметры подключения
            {!parametersOpen && src && tgt && <span className="block mt-1 text-text-primary break-words">
              {rackLabel(src, currentDc)} U{srcU} → {rackLabel(tgt, currentDc)} U{tgtU}
            </span>}
          </summary>
          <RackParametersPanel
          dcs={dcs}
          dcCode={dcCode}
          currentDc={currentDc}
          racks={racks}
          src={src}
          tgt={tgt}
          srcU={srcU}
          tgtU={tgtU}
          cable={cable}
          loading={loading}
          setSrc={setSrc}
          setTgt={setTgt}
          setSrcU={setSrcU}
          setTgtU={setTgtU}
          setCable={(c) => { setCable(c); setResult(null); }}
          handleSwap={handleSwap}
          handleCalc={handleCalc}
        />
        </details>

        {/* ── Results section ── */}
        {!racksLoading && <SummaryPanel
          result={selectedResult}
          primaryResult={result}
          selectedIndex={selectedIndex}
          onSelectRoute={index => setSelection({ result, index })}
          error={error}
          src={src}
          tgt={tgt}
          srcU={srcU}
          tgtU={tgtU}
          dcCode={dcCode}
          cable={cable}
          racks={racks}
          currentDc={currentDc}
          history={history}
          restoreHistory={entry => {
            setSelection({ result: null, index: 0 })
            restoreHistory(entry)
          }}
        />}
      </section>

      {/* ── Right: full SVG DC map ── */}
      <section id="plan-panel" aria-label="План маршрута" className="calculator-plan">
        {racksLoading ? <p role="status" className="p-4 text-sm text-text-secondary">Загрузка стоек…</p> : <DcMapSVG
          racks={racks}
          route={selectedResult?.route ?? []}
          sharedSegments={sharedRouteSegments(selectedResult)}
          referenceRoute={selectedIndex > 0 ? result?.route : undefined}
          routeSegments={selectedResult?.route_segments}
          sourceU={srcU}
          targetU={tgtU}
          routeWorkbench
          srcId={src?.id}
          tgtId={tgt?.id}
          onRackClick={handleRackClick}
          dc={currentDc}
        />}
      </section>
      </div>
    </div>
  )
}
